import { Component } from '@theme/component';

/**
 * @typedef {Object} KlaviyoSignupRefs
 * @property {HTMLFormElement} [formWrapper] - Email form (2-step)
 * @property {HTMLInputElement} [emailInput] - Email input (2-step)
 * @property {HTMLElement} [errorMessage] - Email error container (2-step)
 * @property {HTMLElement} [errorText] - Email error text (2-step)
 * @property {HTMLElement} successWrapper - Success message
 * @property {HTMLElement} [smsWrapper] - SMS step container (2-step)
 * @property {HTMLFormElement} [smsForm] - SMS form (2-step)
 * @property {HTMLInputElement} [phoneInput] - Phone input (2-step)
 * @property {HTMLElement} [smsErrorMessage] - SMS error container (2-step)
 * @property {HTMLElement} [smsErrorText] - SMS error text (2-step)
 * @property {HTMLButtonElement} [smsSkipButton] - SMS skip button (2-step)
 * @property {HTMLFormElement} [combinedForm] - Combined form (1-step)
 * @property {HTMLInputElement} [combinedEmailInput] - Combined email input
 * @property {HTMLInputElement} [combinedPhoneInput] - Combined phone input
 * @property {HTMLElement} [combinedErrorMessage] - Combined error container
 * @property {HTMLElement} [combinedErrorText] - Combined error text
 */

/**
 * @extends {Component<KlaviyoSignupRefs>}
 */
class KlaviyoSignup extends Component {
  /** @type {string|null} Tracks the current active step for editor preview */
  #editorPreviewStep = null;

  /** @type {boolean} Dedupe flag — only send the FB Lead event once per instance */
  #hasSentFbEvent = false;

  handleSubmit(event) {
    event.preventDefault();

    const errorEl = this.refs.errorMessage;
    const emailInput = this.refs.emailInput;

    if (errorEl) errorEl.hidden = true;

    const email = emailInput?.value;
    if (!email) return;

    const apiKey = this.dataset.apiKey;
    const listId = this.refs.formWrapper?.dataset.listId;
    const customSource = this.refs.formWrapper?.dataset.customSource || 'Website Email Signup';

    if (!apiKey || !listId) {
      this.#showError('Email signup is not configured.');
      return;
    }

    // Fire email subscription immediately — don't block UI
    this.#subscribeEmail(apiKey, listId, email, customSource);

    // Update Klaviyo profile with UTMs and custom properties
    this.#updateProfile(apiKey, email);

    // Identify with Klaviyo JS if available (links browser session to profile)
    this.#klaviyoIdentify(email);

    // Fire Meta Lead pixel (consent-gated, deduped)
    this.#sendFacebookTracking();

    if (this.refs.smsWrapper) {
      this.#showSmsStep();
    } else {
      this.#showSuccess();
    }
  }

  async handleSmsSubmit(event) {
    event.preventDefault();

    const smsErrorEl = this.refs.smsErrorMessage;
    if (smsErrorEl) smsErrorEl.hidden = true;

    const phoneInput = this.refs.phoneInput;
    const rawPhone = phoneInput?.value?.trim();
    if (!rawPhone) return;

    const phone = formatE164(rawPhone);
    if (!phone) {
      this.#showSmsError('Please enter a valid phone number.');
      return;
    }

    const apiKey = this.dataset.apiKey;
    const smsListId = this.refs.smsWrapper?.dataset.smsListId;
    const customSource = this.refs.smsWrapper?.dataset.customSource || 'Website SMS Signup';

    if (!apiKey || !smsListId) {
      this.#showSmsError('SMS signup is not configured.');
      return;
    }

    const result = await this.#subscribeSms(apiKey, smsListId, phone, customSource, this.refs.emailInput?.value);

    if (result.ok) {
      this.#showSuccess();
    } else {
      this.#showSmsError(result.message || 'Something went wrong. Please try again.');
    }
  }

  handleSmsSkip() {
    this.#showSuccess();
  }

  async handleCombinedSubmit(event) {
    event.preventDefault();

    const errorEl = this.refs.combinedErrorMessage;
    if (errorEl) errorEl.hidden = true;

    const form = this.refs.combinedForm;
    if (!form) return;

    const email = this.refs.combinedEmailInput?.value?.trim() || '';
    const rawPhone = this.refs.combinedPhoneInput?.value?.trim() || '';
    const optionalField = form.dataset.optionalField || 'none';

    const emailRequired = optionalField !== 'email';
    const phoneRequired = optionalField !== 'phone';

    if (emailRequired && !email) {
      this.#showCombinedError('Please enter your email.');
      return;
    }
    if (phoneRequired && !rawPhone) {
      this.#showCombinedError('Please enter your phone number.');
      return;
    }

    const phone = rawPhone ? formatE164(rawPhone) : null;
    if (rawPhone && !phone) {
      this.#showCombinedError('Please enter a valid phone number.');
      return;
    }

    const apiKey = this.dataset.apiKey;
    const emailListId = form.dataset.listId;
    const smsListId = form.dataset.smsListId;
    const customSource = form.dataset.customSource || 'Website Email + SMS Signup';

    if (!apiKey || (email && !emailListId) || (phone && !smsListId)) {
      this.#showCombinedError('Signup is not configured.');
      return;
    }

    if (email) {
      this.#subscribeEmail(apiKey, emailListId, email, customSource);
      this.#updateProfile(apiKey, email);
      this.#klaviyoIdentify(email);
    }

    if (phone) {
      const result = await this.#subscribeSms(apiKey, smsListId, phone, customSource, email);
      if (!result.ok && !email) {
        this.#showCombinedError(result.message || 'Something went wrong. Please try again.');
        return;
      }
    }

    // Fire Meta Lead pixel (consent-gated, deduped)
    this.#sendFacebookTracking();

    this.#showSuccess();
  }

  /**
   * Shows a specific step for theme editor preview.
   * Called from theme-editor.js on shopify:block:select.
   * @param {string} step - The step to show ('email', 'sms', 'combined', 'success')
   */
  showStep(step) {
    this.#editorPreviewStep = step;
    this.#hideAllSteps();

    if (step === 'email' && this.refs.formWrapper) {
      this.refs.formWrapper.hidden = false;
    } else if (step === 'sms' && this.refs.smsWrapper) {
      this.refs.smsWrapper.hidden = false;
    } else if (step === 'combined' && this.refs.combinedForm) {
      this.refs.combinedForm.hidden = false;
    } else if (step === 'success' && this.refs.successWrapper) {
      this.refs.successWrapper.hidden = false;
    }
  }

  /**
   * Restores the default state (initial form visible).
   * Called from theme-editor.js on shopify:block:deselect.
   */
  resetToDefault() {
    this.#editorPreviewStep = null;
    this.#hideAllSteps();

    if (this.refs.formWrapper) {
      this.refs.formWrapper.hidden = false;
    } else if (this.refs.combinedForm) {
      this.refs.combinedForm.hidden = false;
    }
  }

  /**
   * Fires email subscription to Klaviyo — fire-and-forget, no UI blocking.
   * @param {string} apiKey
   * @param {string} listId
   * @param {string} email
   * @param {string} customSource
   */
  #subscribeEmail(apiKey, listId, email, customSource) {
    fetch('https://a.klaviyo.com/client/subscriptions/?company_id=' + encodeURIComponent(apiKey), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'revision': '2024-10-15'
      },
      body: JSON.stringify({
        data: {
          type: 'subscription',
          attributes: {
            custom_source: customSource,
            profile: {
              data: {
                type: 'profile',
                attributes: { email }
              }
            }
          },
          relationships: {
            list: {
              data: { type: 'list', id: listId }
            }
          }
        }
      })
    }).catch((error) => {
      console.error('Email subscription error:', error);
    });
  }

  /**
   * Fires SMS subscription to Klaviyo. Awaitable so callers can branch on success.
   * @param {string} apiKey
   * @param {string} smsListId
   * @param {string} phone - E.164-formatted phone
   * @param {string} customSource
   * @param {string} [email] - Optional email to attach to the SMS profile
   * @returns {Promise<{ok: boolean, message?: string}>}
   */
  async #subscribeSms(apiKey, smsListId, phone, customSource, email) {
    try {
      const response = await fetch('https://a.klaviyo.com/client/subscriptions/?company_id=' + encodeURIComponent(apiKey), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'revision': '2024-10-15'
        },
        body: JSON.stringify({
          data: {
            type: 'subscription',
            attributes: {
              custom_source: customSource,
              profile: {
                data: {
                  type: 'profile',
                  attributes: {
                    phone_number: phone,
                    email: email || undefined
                  }
                }
              }
            },
            relationships: {
              list: {
                data: { type: 'list', id: smsListId }
              }
            }
          }
        })
      });

      if (response.ok || response.status === 202) {
        return { ok: true };
      }

      const data = await response.json().catch(() => null);
      return { ok: false, message: data?.errors?.[0]?.detail };
    } catch {
      return { ok: false };
    }
  }

  /**
   * Updates the Klaviyo profile with UTM parameters and custom properties.
   * Uses the client profiles API (separate from subscription).
   * @param {string} apiKey
   * @param {string} email
   */
  #updateProfile(apiKey, email) {
    /** @type {Record<string, string>} */
    const properties = {};

    // Capture UTM parameters from URL
    if (this.dataset.trackUtm !== undefined) {
      const params = new URLSearchParams(window.location.search);
      const utmKeys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];

      for (const key of utmKeys) {
        const value = params.get(key);
        if (value) properties[key] = value;
      }
    }

    // Capture custom properties from settings
    const customPropsRaw = this.dataset.customProperties;
    if (customPropsRaw) {
      const pairs = customPropsRaw.split('||');

      for (const pair of pairs) {
        const [key, value] = pair.split('::');
        if (key && value) properties[key.trim()] = value.trim();
      }
    }

    // Only call the API if we have properties to set
    if (Object.keys(properties).length === 0) return;

    fetch('https://a.klaviyo.com/client/profiles/?company_id=' + encodeURIComponent(apiKey), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'revision': '2024-10-15'
      },
      body: JSON.stringify({
        data: {
          type: 'profile',
          attributes: {
            email,
            properties
          }
        }
      })
    }).catch((error) => {
      console.error('Profile update error:', error);
    });
  }

  /**
   * Calls Klaviyo's JS identify to link the browser session to the profile.
   * This enables Klaviyo's onsite tracking (Active on Site, browse history).
   * @param {string} email
   */
  #klaviyoIdentify(email) {
    // @ts-ignore — window.klaviyo is loaded externally
    if (window.klaviyo && typeof window.klaviyo.identify === 'function') {
      // @ts-ignore
      window.klaviyo.identify({ $email: email });
    }
  }

  /**
   * Checks Shopify Customer Privacy consent before firing marketing pixels.
   * Required for Shopify CMP / privacy audit compliance.
   * @returns {boolean}
   */
  #isMarketingAllowed() {
    try {
      // @ts-ignore — window.Shopify.customerPrivacy is provided by Shopify
      const cp = window.Shopify && window.Shopify.customerPrivacy;
      return Boolean(cp && typeof cp.marketingAllowed === 'function' && cp.marketingAllowed());
    } catch {
      return false;
    }
  }

  /**
   * Fires a Meta Lead event via the noscript image-pixel GET endpoint.
   * No fbevents.js / fbq() required — just a 1x1 image request.
   * Consent-gated, deduped per component instance.
   */
  #sendFacebookTracking() {
    if (this.#hasSentFbEvent) return;
    if (this.dataset.enableFacebookTracking === undefined) return;

    const pixelId = this.dataset.facebookPixelId;
    if (!pixelId) return;

    if (!this.#isMarketingAllowed()) return;

    const url =
      'https://www.facebook.com/tr?id=' + encodeURIComponent(pixelId) +
      '&ev=Lead' +
      '&noscript=1' +
      '&dl=' + encodeURIComponent(document.location.href) +
      '&cb=' + Date.now();

    const img = document.createElement('img');
    img.src = url;
    img.width = 1;
    img.height = 1;
    img.alt = '';
    img.style.display = 'none';
    document.body.appendChild(img);

    this.#hasSentFbEvent = true;

    console.log('[FB Lead] fired', { pixelId, url, location: document.location.href });
  }

  #hideAllSteps() {
    if (this.refs.formWrapper) this.refs.formWrapper.hidden = true;
    if (this.refs.smsWrapper) this.refs.smsWrapper.hidden = true;
    if (this.refs.combinedForm) this.refs.combinedForm.hidden = true;
    if (this.refs.successWrapper) this.refs.successWrapper.hidden = true;
  }

  #showSmsStep() {
    if (this.refs.formWrapper) this.refs.formWrapper.hidden = true;
    if (this.refs.smsWrapper) {
      this.refs.smsWrapper.hidden = false;
      this.refs.phoneInput?.focus();
    }
  }

  #showSuccess() {
    this.#hideAllSteps();
    if (this.refs.successWrapper) this.refs.successWrapper.hidden = false;
  }

  #showError(message) {
    const errorEl = this.refs.errorMessage;
    const errorText = this.refs.errorText;
    if (!errorEl) return;

    if (errorText) errorText.textContent = message;
    errorEl.hidden = false;
  }

  #showSmsError(message) {
    const errorEl = this.refs.smsErrorMessage;
    const errorText = this.refs.smsErrorText;
    if (!errorEl) return;

    if (errorText) errorText.textContent = message;
    errorEl.hidden = false;
  }

  #showCombinedError(message) {
    const errorEl = this.refs.combinedErrorMessage;
    const errorText = this.refs.combinedErrorText;
    if (!errorEl) return;

    if (errorText) errorText.textContent = message;
    errorEl.hidden = false;
  }
}

/**
 * Formats a phone number to E.164 format (US/CA numbers).
 * Strips non-digit characters, prepends +1 if needed.
 * @param {string} input - Raw phone number string
 * @returns {string|null} E.164 formatted number or null if invalid
 */
const formatE164 = (input) => {
  const digits = input.replace(/\D/g, '');

  if (digits.length === 10) return '+1' + digits;
  if (digits.length === 11 && digits.startsWith('1')) return '+' + digits;
  if (input.startsWith('+') && digits.length >= 10 && digits.length <= 15) return '+' + digits;

  return null;
};

if (!customElements.get('klaviyo-signup')) {
  customElements.define('klaviyo-signup', KlaviyoSignup);
}
